import type { Experiment } from 'types';
import type { BriefCardCopy } from '../Description';
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HelpTip } from '../../ExperimentSettings/TargetingHelp';
import { ScreenshotList } from './ScreenshotList';

const EXPERIMENT_COPY: BriefCardCopy = {
  title: 'Variant previews',
  description: 'Screenshots captured from the visual editor for each variant.',
};

type ScreenshotsProps = {
  experiment: Experiment;
  copy?: BriefCardCopy;
  /** A tab's label in place of the variant's name and control mark (a flag's Off and On). */
  labelFor?: (variant: Experiment['variants'][number]) => string;
};

export const Screenshots = ({ experiment, copy = EXPERIMENT_COPY, labelFor }: ScreenshotsProps) => {
  const { variants, controlVariantId } = experiment;
  // Default to second variant (usually the first non-control) or first variant
  const defaultVariantId = variants[1]?.id ?? variants[0]?.id ?? null;
  const [variantId, setVariantId] = useState<number | null>(defaultVariantId);

  const onTabChange = (value: string) => {
    // Brief null to trigger re-render for new variant
    setVariantId(null);
    setTimeout(() => {
      setVariantId(+value);
    }, 100);
  };

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="brief-screenshots">
      <div className="border-line border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="micro-label text-foreground">{copy.title}</h2>
          {copy.help && <HelpTip text={copy.help} label={copy.title.toLowerCase()} />}
        </div>
        <p className="supporting mt-1">{copy.description}</p>
      </div>
      <div className="p-4">
        <Tabs value={variantId?.toString() || ''} onValueChange={onTabChange}>
          <TabsList className="border-line bg-panel2 h-auto w-fit flex-wrap gap-1 rounded-lg border p-1">
            {variants.map((v: any) => {
              const isControl = controlVariantId ? v.id === controlVariantId : v.name === 'Original';
              return (
                <TabsTrigger
                  key={v.id}
                  value={v.id.toString()}
                  className="text-dim data-[state=active]:bg-field data-[state=active]:text-foreground rounded-lg px-3 py-1.5 font-mono text-micro font-normal shadow-none data-[state=active]:shadow-none"
                >
                  {labelFor ? labelFor(v) : v.name}
                  {isControl && !labelFor && <span className="text-dim ml-1">(control)</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        {variantId && <ScreenshotList variantId={variantId} experimentId={experiment.id} />}
      </div>
    </section>
  );
};
